import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { INSIGHT_RANGES } from "../../../shared/goals/insights";
import { useNative } from "../../../shared/phone/media";
import { metaSettings } from "../../../shared/goals/meta";
import type { Spot } from "../../../shared/goals/placements";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { ccApi, type AdShape, type Dates, type InsightAd, type Insights, type MetaRow, type Need, type ReadyLite, type ReadyStep, type Results } from "../../lib/ccApi";
import { useOnShown } from "../../lib/inApp";
import { lastLoaded, loadKept } from "../../lib/kept";
import type { GoalView } from "../../lib/goalsApi";
import { readGoal } from "../goal/text";
import { everyField, fieldValue, formatter, main, RANKING, SHOWN, type Field, type Main } from "../results/metrics";
import { cardNumbers, placeNumbers, postNumbers } from "../results/shapes";
import { GoalChat } from "./GoalChat";
import { GoalNative } from "./GoalNative";
import { Mark } from "./Layout";
import { PlaceSheet, spotsPill } from "./PlaceSheet";
import { ReadyChecklist } from "./ReadySteps";
import { adsDay, needText, shortGoal, totalSpent, usd } from "./text";
import { errText, useCcToast, useHome } from "./useCc";
import { datesInWords, dayMonth, paceLine } from "./v2";
import { DatePick, ExplainLine, Fold, PaceBar, Status } from "./V2Parts";
import { useCan } from "../../context/AuthContext";
import "../../styles/wizard.css";
import "../../styles/cc.css";
import "../../styles/results.css";
import "../../styles/command-v2.css";

/**
 * A goal's page (Command Center V2, notes/COMMAND-CENTER-V2.md): while it runs, its pace, one sentence on the
 * dates picked, one line per ad that opens to everything Meta reports about it, where the ads showed, and every
 * other number folded. The goal's one chat docks beside the numbers; Explain lights the part it is about.
 */

const PREF = "results.fields";
const COLORS = ["#2bb3af", "#a78bfa", "#f2cc8f", "#60a5fa", "#f472b6", "#a3e635"];
export const SHAPE: Record<AdShape, [string, string]> = { video: ["play", "Video"], photo: ["photo", "Photo"], carousel: ["photo", "Photos people swipe"], post: ["doc", "A post from your Page"] };

/** Meta's review of an ad, in plain words; null while it runs. */
export function reviewPill(ad: InsightAd): [string, string] | null {
  const s = (ad.review?.status ?? "").toUpperCase();
  if (["PENDING_REVIEW", "IN_PROCESS", "IN_REVIEW"].includes(s)) return ["live", "In review"];
  if (["DISAPPROVED", "REJECTED"].includes(s)) return ["bad", "Rejected by Meta"];
  if (["WITH_ISSUES"].includes(s)) return ["tight", "Needs a fix"];
  if (["PAUSED", "ADSET_PAUSED", "CAMPAIGN_PAUSED"].includes(s) || ad.state === "paused") return ["plain", "Paused"];
  if (["ACTIVE", "PREAPPROVED", "APPROVED"].includes(s) || ad.state === "live") return null;
  return ["plain", ad.adId ? "Not running" : "Added outside Flashadz"];
}

export const pctOf = (n: number | null, total: number | null) => (n && total ? Math.round((100 * n) / total) : 0);
export type Fmt = ReturnType<typeof formatter>;

/** The goal's words with what it counts and its day marked, as the goal step shows them. */
export function Title({ sentence }: { sentence: string }) {
  const r = readGoal(sentence);
  const out: ReactNode[] = [];
  let at = 0;
  for (const [a, b] of r.marks) {
    out.push(sentence.slice(at, a), <mark key={a}>{sentence.slice(a, b)}</mark>);
    at = b;
  }
  out.push(sentence.slice(at));
  return <span>{out}</span>;
}

export interface MetricGroup {
  name: string;
  /** Each number: its name, its value and where it comes from. */
  rows: [string, string, string][];
}

/** Meta's numbers in groups under Meta's names; video numbers only when the ads hold a video. */
export function metricGroups({ m, f, row, perAd, video, post }: { m: Main; f: Fmt; row: MetaRow | null; perAd: boolean; video: string | null; post: ReturnType<typeof postNumbers> | null }): MetricGroup[] {
  const groups: MetricGroup[] = [
    {
      name: "Delivery",
      rows: [
        ["Times shown", f.count(m.impressions), "Impressions, in Ads Manager"],
        ["People reached", f.count(m.reach), "People who saw them at least once"],
        ["Times each person saw it", f.ratio(m.frequency), "On average. Frequency, in Ads Manager"],
        ["Cost per 1,000 views", f.money(m.cpm), "CPM, in Ads Manager"],
      ],
    },
    {
      name: "Clicks",
      rows: [
        ["Link clicks", f.count(m.linkClicks), "Taps that opened the form or page"],
        ["Click rate", f.metaPct(m.ctr), "Link clicks per 100 times shown. CTR, in Ads Manager"],
        ["Cost per click", f.money(m.cpc), "Cost per link click. CPC, in Ads Manager"],
        ...(m.pageViews !== null ? ([["Visits to your page", f.count(m.pageViews), "Taps that loaded the page"]] as [string, string, string][]) : []),
      ],
    },
  ];
  if (video !== null)
    groups.push({
      name: video ? `Video · ${video}` : "Video",
      rows: [
        ["3-second plays", f.count(m.plays), "Watched at least 3 seconds"],
        ["Stopped to watch", f.pct(m.hook), "3-second plays per time shown, worked out from Meta's numbers"],
        ["Watched 15 seconds", f.count(m.thruplays), "Or to the end. ThruPlays, in Ads Manager"],
        ["Kept watching", f.pct(m.hold), "15-second views per 3-second play, worked out from Meta's numbers"],
        ["Average watch time", f.seconds(m.watch), "Meta's average play time"],
      ],
    });
  if (post)
    groups.push({
      name: "On the post",
      rows: [
        ["Reactions", f.count(post.reactions), "Likes and other reactions on the post"],
        ["Comments", f.count(post.comments), ""],
        ["Shares", f.count(post.shares), ""],
        ["Saves", f.count(post.saves), "People who saved the post"],
        ["Profile visits", f.count(post.profileVisits), "People who opened your profile from it"],
      ],
    });
  if (m.purchases !== null || m.value !== null)
    groups.push({
      name: "Sales",
      rows: [
        ["Purchases", f.count(m.purchases), "As Meta counts them"],
        ["Purchase value", f.money(m.value), "Their value, as Meta counts it"],
        ["Sales per $1 spent", f.ratio(m.roas), "Purchase value per $1 spent. ROAS, in Ads Manager"],
      ],
    });
  if (perAd && row?.quality_ranking !== undefined)
    groups.push({
      name: "Meta's rankings",
      rows: [
        ["Quality", RANKING[String(row.quality_ranking)] ?? String(row.quality_ranking), "Against ads competing for the same people"],
        ["Engagement rate", RANKING[String(row.engagement_rate_ranking)] ?? String(row.engagement_rate_ranking ?? "none"), "Against ads competing for the same people"],
        ["Conversion rate", RANKING[String(row.conversion_rate_ranking)] ?? String(row.conversion_rate_ranking ?? "none"), "Against ads with the same goal"],
      ],
    });
  return groups;
}

function Groups({ m, f, row, perAd, video, post, added, fields, onAdd, onRemove }: { m: Main; f: Fmt; row: MetaRow | null; perAd: boolean; video: string | null; post: ReturnType<typeof postNumbers> | null; added: string[]; fields: Map<string, Field>; onAdd: () => void; onRemove: (k: string) => void }) {
  const groups = metricGroups({ m, f, row, perAd, video, post });
  return (
    <div className="rs-all">
      {groups.map((g) => (
        <div className="rs-g" key={g.name}>
          <span className="afl">{g.name}</span>
          {g.rows.map(([n, v, t]) => (
            <div className="rs-m" key={n} title={t || undefined}>
              <span>{n}</span>
              <b>{v}</b>
            </div>
          ))}
        </div>
      ))}
      <div className="rs-g">
        <span className="afl">Added by you</span>
        {added.map((k) => {
          const def = fields.get(k);
          return (
            <div className="rs-m" key={k} title={def?.meta ?? k}>
              <span>{def?.name ?? k}</span>
              <b>{f.field(def ?? { kind: "count" }, fieldValue(row, k))}</b>
              <button type="button" className="rs-x sm" onClick={() => onRemove(k)} aria-label={`Remove ${def?.name ?? k}`}>
                <Icon name="x" />
              </button>
            </div>
          );
        })}
        <button type="button" className="add" onClick={onAdd}>
          <Icon name="plus" />
          Add a number
        </button>
      </div>
    </div>
  );
}

/** From shown to the result; the 3-second step only for a video. */
export const funnelSteps = (m: Main, label: string, video: boolean): [string, number | null][] => [
  ["Shown", m.impressions],
  ...(video ? ([["Stopped 3 s", m.plays]] as [string, number | null][]) : []),
  ["Tapped", m.linkClicks],
  ...(m.pageViews !== null ? ([["Page loaded", m.pageViews]] as [string, number | null][]) : []),
  [label, m.results],
];

function Funnel({ m, f, label, video }: { m: Main; f: Fmt; label: string; video: boolean }) {
  const steps = funnelSteps(m, label, video);
  return (
    <ol className="rs-funnel" aria-label="From shown to result">
      {steps.map(([l, v], i) => {
        const prev = i ? steps[i - 1][1] : null;
        return (
          <li key={l}>
            <b>{f.count(v)}</b>
            <span>{l}</span>
            {i > 0 && prev && v !== null ? <em>{f.pct(v / prev, v / prev < 0.1 ? 1 : 0)}</em> : null}
          </li>
        );
      })}
    </ol>
  );
}

/** Where the ads showed, one row per spot. */
function Spots({ rows, f, label, top }: { rows: ReturnType<typeof placeNumbers>; f: Fmt; label: string; top?: number }) {
  const shown = top ? rows.slice(0, top) : rows;
  const max = shown[0]?.share || 1;
  return (
    <div className="wtab">
      <div className="wrow h">
        <span>Spot</span>
        <span>Share of the money</span>
        <span>Spent</span>
        <span>{label}</span>
        <span>Cost each</span>
      </div>
      {shown.map((r) => (
        <div className="wrow" key={r.key}>
          <span className="wn">{r.name}</span>
          <span className="wbar">
            <span>
              <i style={{ width: `${(100 * r.share) / max}%` }} />
            </span>
            <em>{Math.round(r.share * 100)}%</em>
          </span>
          <b data-l="Spent">{f.money(r.spend)}</b>
          <b data-l={label}>{f.count(r.results)}</b>
          <b data-l="Cost each" className={r.costEach === null ? "none" : ""}>
            {f.money(r.costEach)}
          </b>
        </div>
      ))}
      {top && rows.length > top && <p className="rs-hint">and {rows.length - top} more</p>}
    </div>
  );
}

/** Where a goal's numbers for some dates are kept. */
const metaKey = (goalId: string, d: Dates) => `meta:${goalId}:${"preset" in d ? d.preset : `${d.from}..${d.to}`}`;

/**
 * What a goal's page reads and does, for the page on a computer and the native screen on a phone and an iPad:
 * Meta's numbers for the dates picked, the stored results behind the pace, getting ready, the numbers the
 * owner added, and where the ads show.
 */
export function useGoalNumbers(view: GoalView, reload: () => Promise<void>) {
  const g = view.goal;
  const goalId = g.id;
  const config = view.current?.config ?? null;
  const label = config?.result.label ?? "results";
  const Label = label.charAt(0).toUpperCase() + label.slice(1);
  const { home } = useHome();
  const { toast, say } = useCcToast();
  const product = useMemo(() => home?.products.find((p) => p.goals.some((x) => x.id === goalId)) ?? null, [home, goalId]);
  const homeGoal = useMemo(() => product?.goals.find((x) => x.id === goalId) ?? null, [product, goalId]);

  const [dates, setDates] = useState<Dates>({ preset: "last_7d" });
  const [data, setData] = useState<Insights | null | undefined>(() => lastLoaded<{ v: Insights | null }>(metaKey(goalId, { preset: "last_7d" }))?.v);
  const [stored, setStored] = useState<Results | null>(() => lastLoaded(`results:${goalId}`));
  const [ready, setReady] = useState<ReadyLite | null>(null);
  const [steps, setSteps] = useState<ReadyStep[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  const changedAdded = useRef(false);

  const running = g.state === "live" || g.state === "paused" || g.state === "done";
  const chosen = config ? (metaSettings(config.channels.meta?.settings).placements ?? null) : null;
  /** Save where the ads show; on Meta it goes there first, so the answer may be that it is still on its way. */
  const saveSpots = async (next: Spot[] | null) => {
    try {
      const r = await ccApi.placements(g.id, next);
      const why = r.result && typeof r.result.error === "string" ? r.result.error : null;
      if (r.outcome === "failed" || r.outcome === "refused") {
        say(why ?? "It could not run.", { done: false });
        return false;
      }
      const waiting = r.result && typeof r.result.waiting === "string" ? r.result.waiting : null;
      if (r.state === "approved") say(waiting ?? "It runs in a few minutes.");
      else say(next ? `Saved. The ads show only in the ${next.length} spots you chose.` : "Saved. Meta picks the spots.");
      await reload();
      return true;
    } catch (e) {
      say(errText(e), { done: false });
      return false;
    }
  };
  const load = useCallback(
    async (d: Dates, refresh = false) => {
      setBusy(true);
      try {
        const read = () => ccApi.meta(goalId, d, refresh).then((r) => ({ v: r.results }));
        setData((refresh ? await read() : await loadKept(metaKey(goalId, d), read)).v);
        setError(null);
      } catch (e) {
        setError(errText(e));
        setData((x) => (x === undefined ? null : x));
      } finally {
        setBusy(false);
      }
    },
    [goalId],
  );
  useEffect(() => {
    void load(dates);
  }, [dates, load]);
  useOnShown(() => void load(dates));
  useEffect(() => {
    if (running) loadKept(`results:${goalId}`, () => ccApi.results(goalId)).then(setStored, () => setStored(null));
    if (g.state === "getting_ready") {
      ccApi.ready(goalId).then(setReady, () => setReady(null));
      ccApi.readySteps(goalId).then((r) => setSteps(r.steps), () => setSteps([]));
    }
  }, [goalId, running, g.state]);
  useEffect(() => {
    // Why: a number added before this read returns must not be undone by it.
    void ccApi.pref(PREF).then((r) => !changedAdded.current && setAdded(Array.isArray(r.value) ? (r.value as string[]) : []), () => undefined);
  }, []);

  const f = useMemo(() => formatter(data?.currency ?? null), [data?.currency]);
  const fields = useMemo(() => {
    const all = new Map<string, Field>();
    for (const row of [data?.set ?? null, ...(data?.ads ?? []).map((a) => a.row)]) for (const x of everyField(row)) if (!all.has(x.key) && !SHOWN.has(x.key) && x.key !== "results") all.set(x.key, x);
    return all;
  }, [data]);
  const saveAdded = (next: string[]) => {
    changedAdded.current = true;
    setAdded(next);
    void ccApi.setPref(PREF, next).catch((e: unknown) => say(errText(e), { done: false }));
  };
  const toggleField = (k: string) => saveAdded(added.includes(k) ? added.filter((x) => x !== k) : [...added, k]);

  const dateLabel = data?.range.label ?? ("preset" in dates ? (INSIGHT_RANGES.find(([k]) => k === dates.preset)?.[1] ?? "") : `${dates.from} to ${dates.to}`);
  const inWords = datesInWords(dates, dateLabel);

  const p = stored && config ? paceLine(stored, config.result, g.launchedAt, g.state === "done") : null;
  const spentCents = stored ? totalSpent(stored) : 0;
  const ads = data?.ads ?? [];
  const setMain = main(data?.set ?? null);
  const perAd = ads.map((a) => main(a.row));
  const videos = ads.filter((a) => a.shape === "video" || a.shape === null).length;
  const live = perAd.map((m, i) => ((m.results ?? 0) >= 1 && (m.spend ?? 0) > 0 ? i : -1)).filter((i) => i >= 0);
  const cheapest = live.length > 1 ? live.reduce((b, i) => ((perAd[i].costEach ?? Infinity) < (perAd[b].costEach ?? Infinity) ? i : b)) : -1;
  const spots = placeNumbers(data?.places?.set ?? null);
  const limit = config ? config.money.perResultMaxCents / 100 : null;
  const over = setMain.costEach !== null && limit !== null && setMain.costEach > limit;

  return { config, label, Label, product, homeGoal, toast, say, dates, setDates, data, stored, ready, steps, busy, error, added, running, chosen, saveSpots, load, f, fields, toggleField, dateLabel, inWords, p, spentCents, ads, setMain, perAd, videos, live, cheapest, spots, limit, over };
}

/**
 * A goal's page: the native screen on a phone and an iPad (wherever it is drawn, Home's pane included), today's
 * page on a computer.
 */
export function GoalPage(props: { view: GoalView; reload: () => Promise<void>; chatOpen?: boolean }) {
  return useNative() ? <GoalNative view={props.view} reload={props.reload} /> : <GoalPageWide {...props} />;
}

function GoalPageWide({ view, reload, chatOpen = false }: { view: GoalView; reload: () => Promise<void>; chatOpen?: boolean }) {
  const can = useCan();
  const g = view.goal;
  const goalId = g.id;
  const goalPath = `/goals/${encodeURIComponent(goalId)}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { config, label, Label, product, homeGoal, toast, dates, setDates, data, stored, ready, steps, busy, error, added, running, chosen, saveSpots, load, f, fields, toggleField, dateLabel, inWords, p, spentCents, ads, setMain, perAd, videos, live, cheapest, spots, limit, over } = useGoalNumbers(view, reload);
  const [needs, setNeeds] = useState<Need[]>([]);
  const [picking, setPicking] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Why: a question asked on the home arrives with its answer on the way, so the chat opens to show it.
  const [chat, setChat] = useState<{ open: boolean; src: string | null; ask: { text: string; of?: Dates & { adId?: string }; key: number } | null; wrap: number }>(() => ({ open: chatOpen || !!(location.state as { runId?: string } | null)?.runId, src: null, ask: null, wrap: 0 }));
  const [placing, setPlacing] = useState(false);
  const fold = (k: string) => setOpen((s) => new Set(s.has(k) ? [...s].filter((x) => x !== k) : [...s, k]));

  const explain = (src: string, text: string, adId?: string) => setChat((c) => ({ ...c, open: true, src, ask: { text, of: { ...dates, ...(adId ? { adId } : {}) }, key: Date.now() } }));
  const lit = (src: string) => (chat.open && chat.src === src ? " explaining" : "");
  // Why: Explain asks in the goal's chat, which is an Editor's.
  const explainBtn = ({ src, text, adId, small = false }: { src: string; text: string; adId?: string; small?: boolean }) =>
    !can("editor") ? null : chat.open && chat.src === src ? (
      <button type="button" className="btn sm ghost explaining" aria-pressed="true" onClick={() => setChat((c) => ({ ...c, open: true }))}>
        <Icon name="spark" />
        Explaining
      </button>
    ) : (
      <button type="button" className={`btn sm ghost${small ? " ic" : ""}`} aria-pressed="false" aria-label="Explain" title="Explain" onClick={() => explain(src, text, adId)}>
        <Icon name="spark" />
        {!small && "Explain"}
      </button>
    );

  // --- What the page shows ------------------------------------------------------------------------

  const focus = chat.open && chat.src ? " focus" : "";

  const head = (
    <>
      <div className="ctx">
        <div className="crumb">
          <Link to="/command">
            <Icon name="left" />
            Command Center
          </Link>
          {product && (
            <>
              <span className="sep" />
              <Link to={`/products/${encodeURIComponent(product.id)}`}>
                <Mark p={product} />
                <b>{product.name}</b>
              </Link>
            </>
          )}
        </div>
        <div className="acts">
          {g.state !== "done" && (
            <Link className="btn sm quiet" to={`${goalPath}/plan`}>
              <Icon name="doc" />
              <span className="bl">Plan</span>
            </Link>
          )}
          {(g.state === "live" || g.state === "paused" || g.state === "getting_ready") && can("editor") && (
            <Link className="btn sm quiet" to={`${goalPath}/edit`}>
              <Icon name="pen" />
              <span className="bl">Edit goal</span>
            </Link>
          )}
          {g.state === "done" && (
            <button type="button" className="btn sm quiet" onClick={() => setChat((c) => ({ ...c, wrap: c.wrap + 1 }))}>
              <Icon name="doc" />
              <span className="bl">Wrap-up</span>
            </button>
          )}
          <button type="button" className={`btn sm ghost${chat.open ? " on" : ""}`} aria-pressed={chat.open} onClick={() => setChat((c) => ({ ...c, open: !c.open, src: c.open ? null : c.src }))}>
            <Icon name="spark" />
            <span className="bl">Ask</span>
          </button>
        </div>
      </div>
      <div className="live-h">
        <h1 className="long">
          <Title sentence={g.sentence} />
        </h1>
        {data !== null && <DatePick dates={dates} onChange={setDates} launchedAt={g.launchedAt} readDays={data ? [data.range.from, data.range.to] : undefined} />}
      </div>
      <p className="goal-line">
        {g.state === "getting_ready" ? <Status kind="ready" /> : g.state === "planning" || g.state === "plan_ready" ? <Status kind="planning" /> : p ? <Status kind={p.kind} paused={g.state === "paused"} /> : null}
        {config && (
          <span>
            {usd(config.money.budgetCents)} budget · most {usd(config.money.perResultMaxCents)} each
          </span>
        )}
        {g.launchedAt ? <span>Launched {dayMonth(new Date(g.launchedAt))}</span> : homeGoal && adsDay(homeGoal) ? <span>Ads go live {adsDay(homeGoal)}</span> : null}
      </p>
    </>
  );

  const hero = p && config && (
    <section className={`card hero${p.kind === "behind" ? " behind" : ""}`}>
      <PaceBar p={p} label={label} big />
      <div className="money2">
        <div className={`pace${spentCents / config.money.budgetCents > p.gone / p.all + 0.05 ? " behind" : ""}`}>
          <div className="pace-l">
            <span className="n">{usd(Math.round(spentCents / 100) * 100)}</span>
            <span className="of">of {usd(config.money.budgetCents)} spent</span>
          </div>
          <div className="track">
            <i className="fill" style={{ width: `${Math.min(100, (100 * spentCents) / config.money.budgetCents)}%` }} />
            {p.kind !== "done" && <u style={{ left: `${(100 * p.gone) / p.all}%` }} title={`Time gone: ${pctOf(p.gone, p.all)}%`} />}
          </div>
        </div>
        <p className="rs-hint">{p.kind === "done" ? `${f.money(stored?.totals.results ? spentCents / 100 / stored.totals.results : null)} each, most ${usd(config.money.perResultMaxCents)}.` : `${pctOf(spentCents, config.money.budgetCents)}% of the money for ${pctOf(p.gone, p.all)}% of the time. ${p.all - p.gone} days left.`}</p>
      </div>
    </section>
  );

  const needsList = needs.length > 0 && (
    <section className="nv" aria-label="Needs you">
      <div className="nv-h">
        Needs you<b>{needs.length}</b>
      </div>
      {needs.map((n) => (
        <button type="button" className="nv-r" key={n.id} onClick={() => navigate(`${goalPath}?need=${encodeURIComponent(n.id)}`)}>
          <span className="nd" />
          <span className="nv-t">
            <b>{needText(n).title}</b>
          </span>
          <em className="nv-w" />
          <Icon name="right" />
        </button>
      ))}
    </section>
  );

  const setup =
    g.state === "getting_ready" ? (
      <ReadyChecklist goalPath={goalPath} steps={steps} ready={ready} view={view} />
    ) : g.state === "planning" || g.state === "plan_ready" ? (
      <section className="card ready-c">
        <div className="card-h">
          <h3>{g.state === "plan_ready" ? "A new plan is ready" : "The agent is writing a new plan"}</h3>
          <Link className={`btn sm${g.state === "plan_ready" ? "" : " ghost"}`} to={`${goalPath}/edit`}>
            {g.state === "plan_ready" ? "Review the plan" : "New plan"}
          </Link>
        </div>
      </section>
    ) : null;

  const numbers =
    data === undefined ? (
      running ? <p className="load-note">Reading Meta's numbers…</p> : null
    ) : data === null ? (
      running ? (
        <section className="card rs-empty">
          <h3>No numbers yet</h3>
          <p className="rs-hint">Results show here once the ads run on Meta.</p>
        </section>
      ) : null
    ) : (
      <>
        {data.stale && (
          <p className="rs-stale">
            Meta could not be read just now ({data.error}). These are the numbers read {new Date(data.readAt).toLocaleString()}.
          </p>
        )}
        <section className={`lead${lit("set")}`} data-lit>
          <p>
            {setMain.spend ? (
              <>
                <mark>{f.money(setMain.spend)}</mark> brought <mark>{f.count(setMain.results)}</mark> {label} {inWords}
                {setMain.costEach !== null && (
                  <>
                    , <mark className={over ? "w" : ""}>{f.money(setMain.costEach)}</mark> each, {over ? <mark className="w">over your {usd(config!.money.perResultMaxCents)} most per result</mark> : limit !== null ? <>under your {usd(config!.money.perResultMaxCents)} most per result</> : null}
                  </>
                )}
                .
              </>
            ) : (
              <>No numbers {inWords}.</>
            )}
          </p>
          {setMain.spend ? explainBtn({ src: "set", text: `Explain all the ads together, ${dateLabel.toLowerCase()}` }) : null}
        </section>
        <section className="card ads2 quiet-part">
          <div className="ads2-h">
            <h3>The ads</h3>
            <span className="rs-hint">Tap one for all its numbers</span>
          </div>
          {ads.map((a, i) => {
            const m = perAd[i];
            const key = `ad:${a.platformAdId}`;
            const isOpen = open.has(key);
            const shape = a.shape ? SHAPE[a.shape] : (["play", "Added outside Flashadz"] as [string, string]);
            const pill = reviewPill(a);
            const ran = (m.impressions ?? 0) > 0;
            const isVideo = a.shape === "video" || a.shape === null;
            const cards = a.shape === "carousel" && a.cards ? cardNumbers(data.cards, a.platformAdId, a.cards) : null;
            const best = cards ? cards.reduce((b, c, j) => ((c.taps ?? 0) > (cards[b].taps ?? 0) ? j : b), 0) : -1;
            const adSpots = placeNumbers(data.places?.ads ?? null, a.platformAdId);
            return (
              <div className={`ar${isOpen ? " open" : ""}${lit(key)}`} data-lit key={a.platformAdId}>
                <div className="ar-m">
                  <button type="button" className="ar-o" aria-expanded={isOpen} onClick={() => fold(key)}>
                    <span className="lad-thumb sm">
                      <Icon name={shape[0] as "play"} />
                    </span>
                    <span className="ar-t">
                      <b>
                        {a.name}
                        {i === cheapest && <span className="tag">Cheapest</span>}
                      </b>
                      <span>
                        {shape[1]}
                        {pill && (
                          <>
                            {" · "}
                            <span className={`pill ${pill[0]}`}>{pill[1]}</span>
                          </>
                        )}
                      </span>
                    </span>
                    {ran && a.row ? (
                      <>
                        <span className="ar-bar" title={`${pctOf(m.spend, setMain.spend)}% of the money`}>
                          <span>
                            <i style={{ width: `${pctOf(m.spend, setMain.spend)}%` }} />
                          </span>
                          <em>{pctOf(m.spend, setMain.spend)}%</em>
                        </span>
                        <span className="ar-n ar-res" title={label}>
                          <b>{f.count(m.results)}</b> results
                        </span>
                        <span className="ar-n ar-each">
                          <b>{f.money(m.costEach)}</b> each
                        </span>
                      </>
                    ) : (
                      <span className="ar-none">{pill?.[1] === "In review" ? "No numbers until Meta approves it." : `No numbers ${inWords}.`}</span>
                    )}
                    <Icon name="chev" />
                  </button>
                  {ran && a.row ? explainBtn({ src: key, text: `Explain ${a.name}, ${dateLabel.toLowerCase()}`, adId: a.adId ?? a.platformAdId, small: true }) : null}
                </div>
                {a.review?.message && (
                  <div className="rs-msg ar-msg">
                    <span className="afl">Meta's message</span>
                    <blockquote>
                      {a.review.message.title && <b>{a.review.message.title}</b>}
                      {a.review.message.text && <p>{a.review.message.text}</p>}
                    </blockquote>
                    {can("editor") && (
                      <Link className="btn sm ghost" to={`${goalPath}/ready`}>
                        Fix and resubmit
                      </Link>
                    )}
                  </div>
                )}
                {isOpen && ran && a.row && (
                  <div className="ar-d">
                    <div className="rs-tiles">
                      <div className="tl hero">
                        <b>{f.money(m.spend)}</b>
                        <span>Spent</span>
                      </div>
                      <div className="tl hero">
                        <b>{f.count(m.results)}</b>
                        <span>{Label}</span>
                      </div>
                      <div className="tl hero">
                        <b>{f.money(m.costEach)}</b>
                        <span>Cost each</span>
                      </div>
                    </div>
                    <Funnel m={m} f={f} label={Label} video={isVideo} />
                    {cards && (
                      <div className="cards2">
                        <span className="afl">Each card</span>
                        <div className="cards2-r">
                          {cards.map((c, j) => (
                            <div className={`cardc${j === best ? " best" : ""}`} key={c.name}>
                              <div>
                                <b>{c.name}</b>
                                <span>
                                  {c.taps === null ? "No taps yet" : `${f.count(c.taps)} taps`}
                                  {c.ctr !== null ? ` · ${c.ctr.toFixed(2)}% CTR` : ""}
                                </span>
                              </div>
                              {j === best && (c.taps ?? 0) > 0 && <span className="tag">Most taps</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {adSpots.length > 0 && (
                      <div className="ad-where">
                        <span className="afl">Where it showed</span>
                        <Spots rows={adSpots} f={f} label={Label} top={4} />
                      </div>
                    )}
                    <Groups m={m} f={f} row={a.row} perAd video={isVideo ? "" : null} post={a.shape === "post" ? postNumbers(a.row) : null} added={added} fields={fields} onAdd={() => setPicking(true)} onRemove={toggleField} />
                  </div>
                )}
              </div>
            );
          })}
        </section>
        {spots.length > 0 && (
          <section className="card wh2 quiet-part">
            <div className="wh-h">
              <div>
                <h3>Where they showed</h3>
                <p className="rs-hint">
                  Set once for the goal: all {ads.length} ads share these spots.
                </p>
              </div>
              <span className="wh-r">
                <span className="pill live">{spotsPill(chosen)}</span>
                {can("editor") && (
                  <button type="button" className="btn sm ghost" onClick={() => setPlacing(true)}>
                    Change
                  </button>
                )}
              </span>
            </div>
            <p className="say">
              Mostly <mark>{spots[0].name}</mark> ({Math.round(spots[0].share * 100)}%)
              {spots[1] && (
                <>
                  {" "}
                  and <mark>{spots[1].name}</mark> ({Math.round(spots[1].share * 100)}%)
                </>
              )}
              .
              {(() => {
                const cheap = spots.filter((s) => s.costEach !== null).toSorted((x, y) => x.costEach! - y.costEach!)[0];
                return cheap ? (
                  <>
                    {" "}
                    {Label} cost least in <mark>{cheap.name}</mark>, <mark>{f.money(cheap.costEach)}</mark> each.
                  </>
                ) : null;
              })()}
            </p>
            <Fold label="Every spot" open={open.has("spots")} onToggle={() => fold("spots")}>
              <Spots rows={spots} f={f} label={Label} />
            </Fold>
          </section>
        )}
        {!spots.length && (
          <section className="card place-row">
            <span className="eyebrow">Where the ads show</span>
            <span className="pill live">{spotsPill(chosen)}</span>
            {can("editor") && (
              <button type="button" className="btn sm ghost" onClick={() => setPlacing(true)}>
                Change
              </button>
            )}
          </section>
        )}
        <PlaceSheet open={placing} onClose={() => setPlacing(false)} title={shortGoal(g.sentence)} ads={ads.length} onMeta spots={chosen} onSave={saveSpots} />
        <section className="card quiet-part fold-card">
          <Fold label="All of Meta's numbers for these ads" open={open.has("all")} onToggle={() => fold("all")}>
            <div className="rs-tiles sm">
              <div className="tl">
                <b>{f.count(setMain.reach)}</b>
                <span>People reached</span>
              </div>
              <div className="tl">
                <b>{f.ratio(setMain.frequency)}</b>
                <span>Times each person saw it</span>
              </div>
              <div className="tl">
                <b>{f.metaPct(setMain.ctr)}</b>
                <span>Click rate</span>
              </div>
              <div className="tl">
                <b>{f.money(setMain.cpm)}</b>
                <span>Cost per 1,000 views</span>
              </div>
              {videos > 0 && (
                <div className="tl">
                  <b>{f.pct(setMain.hook)}</b>
                  <span>{videos < ads.length ? "Stopped to watch, videos" : "Stopped to watch"}</span>
                </div>
              )}
            </div>
            {live.length > 1 && (
              <div className="rs-money">
                <span className="afl">Where the {label} came from</span>
                <div className="rs-bar">
                  {live.map((i) => (
                    <i key={i} style={{ width: `${pctOf(perAd[i].results, setMain.results)}%`, background: COLORS[i % COLORS.length] }} title={`${ads[i].name}: ${pctOf(perAd[i].results, setMain.results)}%`} />
                  ))}
                </div>
                <div className="rs-key">
                  {live.map((i) => (
                    <span key={i}>
                      <i style={{ background: COLORS[i % COLORS.length] }} />
                      {ads[i].name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Groups m={setMain} f={f} row={data.set} perAd={false} video={videos > 0 ? (videos < ads.length ? `${videos} of ${ads.length} ads` : "") : null} post={null} added={added} fields={fields} onAdd={() => setPicking(true)} onRemove={toggleField} />
            <p className="rs-hint rs-note">
              {dateLabel}
              {data.range.from ? ` (${data.range.from} to ${data.range.to})` : ""}, as Meta reports it, read {new Date(data.readAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.
              {data.dropped.length ? ` Meta does not give this ad account ${data.dropped.length} of its fields.` : ""}{" "}
              <button type="button" className="linkish" disabled={busy} onClick={() => void load(dates, true)}>
                {busy ? "Reading…" : "Read again"}
              </button>
            </p>
          </Fold>
        </section>
      </>
    );

  return (
    <div className={`wrap ga gw ccx rs cv2${focus}`}>
      {head}
      {hero}
      {needsList}
      <div className={`live-wrap${chat.open ? " chat-on" : ""}`}>
        <div className="live-main">
          {error && <p className="cx-err">{error}</p>}
          {setup}
          {numbers}
        </div>
        <GoalChat
          view={view}
          reload={reload}
          dock={{
            open: chat.open,
            onClose: () => setChat((c) => ({ ...c, open: false, src: null })),
            ask: chat.ask,
            dates: dateLabel,
            onNeeds: setNeeds,
            wrapKey: chat.wrap,
          }}
        />
      </div>
      <ExplainLine active={chat.open && !!chat.src} />
      <Sheet open={picking} onClose={() => setPicking(false)} label="Add a number" className="wide">
        <div className="eyebrow">Add a number</div>
        <h2 style={{ marginTop: 6 }}>Every other number Meta reports</h2>
        <p className="lede" style={{ fontSize: "var(--fs-text)" }}>
          Added numbers show on every ad and on all the ads together.
        </p>
        {[...new Set([...fields.values()].map((x) => x.group))].map((grp) => (
          <div className="rs-pick" key={grp}>
            <span className="afl">{grp}</span>
            {[...fields.values()]
              .filter((x) => x.group === grp)
              .map((x) => (
                <button type="button" key={x.key} className={`rs-fp${added.includes(x.key) ? " on" : ""}`} aria-pressed={added.includes(x.key)} onClick={() => toggleField(x.key)}>
                  <span className="bx">
                    <Icon name="check" />
                  </span>
                  <span>
                    <b>{x.name}</b>
                    <code>{x.meta}</code>
                  </span>
                </button>
              ))}
          </div>
        ))}
      </Sheet>
      {toast}
    </div>
  );
}
